import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { CustomButton } from "ui";
import { CONTRACT_STATUS_ENUM, RISK_STATUS_ENUM, BidWalletCheckGridDto, BID_WALLET_OPERATION_ENUM } from "interfaces";
import { confirmationModalConfigs, ConfirmationModal } from "components/ActionModals/ConfirmationModal";
import { approveBid } from "services";
import { NEED_APPROVE_RISK_MANAGER_MESSAGE } from "constants/network";
import { requestWithNotify } from "utils";
import { useNotificationContext } from "providers";

interface IProps {
  walletChecks: BidWalletCheckGridDto[];
}

const REQUEST_DELAY = 1500;

export const ActivateBidButton = observer((props: IProps) => {
  const { actionModals, bidCard } = useStores();
  const { id, destinationWallet, contract } = bidCard.bid;
  const { walletChecks } = props;

  const { openNotification } = useNotificationContext();

  const currentAmlCheckStatus = bidCard.amlCheckStatusData?.find(
    (item) => item.requestAction === BID_WALLET_OPERATION_ENUM.APPROVE_REQUEST
  );

  const contractIsDraft = bidCard.bid.contract.status === CONTRACT_STATUS_ENUM.DRAFT;

  const getDestinationWalletCheckStatus = () => {
    if (!destinationWallet || !walletChecks.length) {
      return { isValid: false, hasApprovalNeeded: false };
    }

    const destinationWalletCheck = walletChecks.find((item) => item.address === destinationWallet);

    if (!destinationWalletCheck) {
      return { isValid: false, hasApprovalNeeded: false };
    }

    const hasApprovalNeeded = destinationWalletCheck.riskStatus === RISK_STATUS_ENUM.APPROVE_NEEDED;

    const isValid = !!destinationWalletCheck.riskStatus && !hasApprovalNeeded;

    return { isValid, hasApprovalNeeded };
  };

  const destinationWalletCheckStatus = getDestinationWalletCheckStatus();

  const activateBidHandler = () => {
    actionModals.open(
      () => (
        <ConfirmationModal
          text="Вы действительно хотите одобрить заявку?"
          successText="Заявка успешно одобрена"
          buttonText="Одобрить"
          calledQuery={() => approveBid({ id })}
          onSuccess={() =>
            requestWithNotify({
              fetchFn: () => bidCard.fetchCardById(id),
              openNotification,
              delay: REQUEST_DELAY,
            })
          }
        />
      ),
      {
        ...confirmationModalConfigs,
        onClose: () => actionModals.closeAll(),
      }
    );
  };

  const getTooltipForButton = () => {
    if (contractIsDraft) return "Договор не активен";
    if (contractIsImported) {
      if (!currentAmlCheckStatus) return undefined;
      if (currentAmlCheckStatus.checksCompleted && !currentAmlCheckStatus.checksSuccess)
        return NEED_APPROVE_RISK_MANAGER_MESSAGE;
    }
    return undefined;
  };

  const contractIsImported = contract.direction === "IMP";

  const isDisabledActivateButton = () => {
    if (contractIsDraft) return true;
    if (contractIsImported) {
      if (!currentAmlCheckStatus) return true;
      if (!(currentAmlCheckStatus.checksSuccess && currentAmlCheckStatus.checksCompleted)) {
        return true;
      }
    }
    return false;
  };

  return (
    <CustomButton onClick={activateBidHandler} disabled={isDisabledActivateButton()} tooltip={getTooltipForButton()}>
      Одобрить заявку
    </CustomButton>
  );
});
